import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Star, DownloadSimple, PlusCircle, X, MagnifyingGlass, ChatsCircle, ListChecks } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { DepartmentPicker, DeptChips, useDepartments } from "../components/DepartmentPicker";

const SENTIMENT = {
  positive: "bg-emerald-50 text-emerald-700 border-emerald-200",
  neutral: "bg-slate-100 text-slate-600 border-slate-200",
  negative: "bg-red-50 text-red-700 border-red-200",
};

const SENTIMENT_FILTERS = [
  { key: "", label: "All" },
  { key: "negative", label: "Negative" },
  { key: "neutral", label: "Neutral" },
  { key: "positive", label: "Positive" },
];

const SOURCES = ["In-person", "Google", "TripAdvisor", "Zomato", "Swiggy", "Yelp"];

export default function ReviewsPage() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ source: "Google", rating: 3, text: "", author: "", department_ids: [] });
  const [busy, setBusy] = useState(false);
  const [simBusy, setSimBusy] = useState(false);
  const [q, setQ] = useState("");
  const [sentiment, setSentiment] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const departments = useDepartments();
  const scoped = !["admin", "developer"].includes(user.role) && (user.department_ids || []).length > 0;

  const load = () => api.get("/reviews", { params: { q, sentiment, department_id: deptFilter } }).then((r) => setReviews(r.data));
  useEffect(() => { load(); }, [q, sentiment, deptFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const simulate = async () => {
    setSimBusy(true);
    try {
      const { data } = await api.post("/reviews/simulate");
      toast.success(`Ingested ${data.length} reviews — AI tagged and routed to briefings`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSimBusy(false);
    }
  };

  const addReview = async () => {
    if (!form.text.trim()) return toast.error("Review text is required");
    setBusy(true);
    try {
      await api.post("/reviews", { ...form, author: form.author || "Guest" });
      toast.success("Review added & classified by AI");
      setShowAdd(false);
      setForm({ source: "Google", rating: 3, text: "", author: "", department_ids: [] });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8 rise-in" data-testid="reviews-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">Review-to-Learn Engine</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">Guest Reviews</h1>
        </div>
        <div className="flex gap-3">
          <button data-testid="reviews-simulate-button" onClick={simulate} disabled={simBusy}
            className="h-12 px-6 rounded-full bg-slate-900 text-white font-bold text-sm flex items-center gap-2 tap-press hover:bg-slate-800 disabled:opacity-50">
            <DownloadSimple size={18} /> {simBusy ? "Ingesting…" : "Simulate ingestion"}
          </button>
          <button data-testid="reviews-add-button" onClick={() => setShowAdd(true)}
            className="h-12 px-6 rounded-full border-2 border-slate-200 font-bold text-sm flex items-center gap-2 tap-press hover:border-slate-900">
            <PlusCircle size={18} /> Add review
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3" data-testid="reviews-filters">
        <div className="relative flex-1">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="reviews-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search reviews, guests, complaint types…"
            className="w-full h-12 pl-12 pr-4 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {!scoped && departments.length > 0 && (
            <select data-testid="reviews-dept-filter" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}
              className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold text-sm">
              <option value="">All departments</option>
              {departments.filter((d) => d.active !== false).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          {SENTIMENT_FILTERS.map((f) => (
            <button key={f.key} data-testid={`reviews-filter-${f.label.toLowerCase()}`} onClick={() => setSentiment(f.key)}
              className={`h-12 px-5 rounded-full text-sm font-bold tap-press border ${
                sentiment === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4" data-testid="reviews-list">
        {reviews.map((r) => (
          <div key={r.id} data-testid={`review-card-${r.id}`}
            className={`bg-white border border-slate-200 rounded-2xl p-6 border-l-4 ${
              r.sentiment === "negative" ? "border-l-red-500" : r.sentiment === "positive" ? "border-l-emerald-500" : "border-l-slate-300"}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-bold text-sm">{r.author}</span>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{r.source}</span>
              <span className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} size={14} weight={i <= r.rating ? "fill" : "regular"} className={i <= r.rating ? "text-amber-400" : "text-slate-200"} />
                ))}
              </span>
            </div>
            <p className="text-slate-700 mt-3 leading-relaxed">{r.text}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full border capitalize ${SENTIMENT[r.sentiment] || SENTIMENT.neutral}`}>{r.sentiment}</span>
              <span className="text-xs font-bold px-3 py-1.5 rounded-full border bg-slate-100 text-slate-600 border-slate-200">{r.complaint_type}</span>
              <span className="text-xs font-bold px-3 py-1.5 rounded-full border bg-slate-100 text-slate-600 border-slate-200 capitalize">Role: {String(r.affected_role).replace("_", " ")}</span>
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full border capitalize ${r.severity === "high" ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.severity} severity</span>
              <DeptChips ids={r.department_ids} departments={departments} testId={`review-dept-chips-${r.id}`} />
            </div>
            {r.sentiment === "negative" && (
              <p className="mt-3 text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-4 py-2.5">
                → Auto-assigned to next briefing: {r.training_action}
              </p>
            )}
            {r.sentiment === "negative" && r.action_pointers?.length > 0 && (
              <div className="mt-3 bg-slate-50 border border-slate-100 rounded-xl p-4" data-testid={`review-pointers-${r.id}`}>
                <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400 flex items-center gap-1.5 mb-2">
                  <ListChecks size={15} /> AI action pointers
                </p>
                <ul className="space-y-1.5">
                  {r.action_pointers.map((p, i) => (
                    <li key={i} className="text-sm font-semibold text-slate-700 flex gap-2">
                      <span className="text-emerald-600 font-black">{i + 1}.</span> {p}
                    </li>
                  ))}
                </ul>
                {r.situational_quiz && (
                  <span data-testid={`review-quiz-badge-${r.id}`}
                    className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-full">
                    <ChatsCircle size={14} weight="duotone" /> Situational micro-quiz routed to briefings
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
        {reviews.length === 0 && (
          <div className="text-center py-16 text-slate-400">
            <p className="font-heading font-bold text-lg">No reviews yet</p>
            <p className="text-sm mt-1">Hit "Simulate ingestion" to pull sample reviews through the AI tagging pipeline.</p>
          </div>
        )}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setShowAdd(false)}>
          <div data-testid="add-review-modal" onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl p-6 sm:p-8 rise-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold">Add guest review</h2>
              <button onClick={() => setShowAdd(false)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <select data-testid="review-source-select" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                  className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold">
                  {SOURCES.map((s) => <option key={s}>{s}</option>)}
                </select>
                <select data-testid="review-rating-select" value={form.rating} onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value) })}
                  className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold">
                  {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
                </select>
              </div>
              <input data-testid="review-author-input" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })}
                placeholder="Guest name (optional)" className="w-full h-12 px-4 rounded-xl border border-slate-200" />
              <textarea data-testid="review-text-input" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="Paste the review text…" rows={4}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 resize-none" />
              <DepartmentPicker label="Review is about department(s)" value={form.department_ids} testId="review-dept-picker"
                onChange={(ids) => setForm({ ...form, department_ids: ids })} />
              <button data-testid="review-submit-button" onClick={addReview} disabled={busy}
                className="w-full h-14 rounded-full bg-slate-900 text-white font-heading font-bold tap-press hover:bg-slate-800 disabled:opacity-50">
                {busy ? "Classifying with AI…" : "Add & classify"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
